/**
 * Presentation mode — focus / review / deliverables share one mechanism.
 * Review / deliverables show the active frame in one comfortable box. Focus
 * treats its frames like managed windows: the main frame large with the
 * others in a column beside it, snap/maximize/restore inside the focus
 * surface. The planners in `lib/focus/` decide the boxes,
 * these commands write the presentation store, and the presentation's
 * renderer draws it. Nothing here is ever persisted — collaborators never
 * see presentation changes.
 * Spec: docs/specs/2026-06-12-focus-review-presentation-mode.md
 */
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import type {
  CustomFocusLayout,
  FocusCardOrigin,
  FocusPlacement,
  PresentationState,
  PresentationVariant,
} from "@/lib/focus/focus-types";
import { useFramesStore } from "@/stores/frames-store";
import { selectOrderedFrames } from "@/stores/frames-selectors";
import { CANVAS_VIEWPORT_TRANSITION_MS } from "@/lib/canvas-viewport-transitions";
import {
  focusPresentedFramesInDesktopMode,
  presentsDesktopFocusHere,
  previewOpenHere,
  shouldUseDesktopExpandedPresentation,
} from "@/lib/focus/focus-desktop-mode";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { scheduleFocusPresentationReflow } from "@/lib/focus-presentation-reflow";
import type {
  FocusWindowBox,
  FocusWindowMode,
} from "@/lib/focus-window-layout";
import { expandConversationCompanionStackFrame } from "@/lib/conversation-companion-focus-layout";
import { transientConversationFrameIds } from "@/lib/transient-conversation-frame";
import { currentPresentationBox } from "@/lib/presentation-frame-box";
import { discardFrames } from "@/lib/frame-ingest";
import { commitPresentation, focusAdapterFor } from "@/lib/focus/focus-adapter";
import {
  planAddFocusFrame,
  planFocusEntry,
  planPresentAt,
} from "@/lib/focus/focus-plan";
import { currentFocusPlanEnv } from "@/lib/focus/focus-natural-size";
import { focusRendererKind } from "@/lib/focus/focus-renderer-kind";

export {
  calculateDenkFocusBox,
  calculatePresentationBox,
  getCurrentDenkFocusBox,
  TOP_MARGIN,
  type PresentationBox,
} from "@/lib/presentation-frame-box";

export { scrubPresentationClasses } from "@/lib/presentation-classes";

function focusViewportDuration(requested?: number): number | undefined {
  return shouldUseDesktopExpandedPresentation() ? 0 : requested;
}

function isConversation(id: string | undefined): boolean {
  return (
    !!id &&
    useFramesStore.getState().framesById[id]?.frameType === "conversation"
  );
}

/** The frames whose desktop placement follows the presentation. */
function presentedFrameIds(presentation: PresentationState): string[] {
  if (presentation.variant === "focus") return presentation.frameIds;
  const activeId = presentation.frameIds[presentation.activeIndex];
  return activeId ? [activeId] : [];
}

export function enterPresentationMode(
  variant: PresentationVariant,
  frameIds: string[],
  startFrameId?: string,
  options: {
    focusViewportDuration?: number;
    focusWindowMode?: FocusWindowMode | null;
    focusLayoutBoxes?: FocusWindowBox[];
    customFocusLayout?: CustomFocusLayout;
    agentContentFit?: PresentationState["agentContentFit"];
    allowDesktopMirror?: boolean;
    preserveMixedFrameTypes?: boolean;
    /** A focus entry's placement; omitted, an open preview stays one and anything else enters focus mode. */
    placement?: FocusPlacement;
    /** A wall card the opening grows from; it plays on desktop too. */
    fromCard?: FocusCardOrigin;
    /** The display a desktop Focus opens on; omitted, this window's. */
    displayId?: number;
  } = {},
) {
  const placement =
    options.placement ?? (previewOpenHere() ? "preview" : "focus");
  const replaced = usePresentationStore.getState().presentation;
  const replacedFocusReturnsToWorkspace =
    replaced?.variant === "focus" &&
    useDesktopMode.getState().compactFrameOverlayReturnsToWorkspace
      ? true
      : undefined;
  if (replaced) {
    const preserveTemporaryFrames =
      variant === "focus" &&
      (isConversation(startFrameId ?? frameIds[0]) ||
        (!!options.preserveMixedFrameTypes && frameIds.some(isConversation)));
    endPresentation(replaced, {
      restoreViewport: false,
      preserveTemporaryFrames,
      replaced: true,
    });
  }
  const focus = variant === "focus";
  const next = planFocusEntry(
    {
      variant,
      renderer: focusRendererKind(),
      displayId: presentsDesktopFocusHere(options.allowDesktopMirror)
        ? (options.displayId ?? getWindowDisplayId())
        : null,
      frameIds,
      startFrameId,
      focusWindowMode: options.focusWindowMode,
      focusLayoutBoxes: options.focusLayoutBoxes,
      customFocusLayout: options.customFocusLayout,
      agentContentFit: options.agentContentFit,
      preserveMixedFrameTypes: options.preserveMixedFrameTypes,
      reveal: focus && !shouldUseDesktopExpandedPresentation(),
      fromCard: focus ? options.fromCard : undefined,
      presentationBox: focus ? undefined : currentPresentationBox(variant),
    },
    currentFocusPlanEnv(),
  );
  if (!next) {
    // The replaced presentation ends with no successor.
    if (replaced) {
      focusAdapterFor(replaced.renderer).restore(replaced, {
        restoreViewport: false,
      });
    }
    return;
  }
  commitPresentation(
    next,
    {
      entering: true,
      duration: focus
        ? focusViewportDuration(
            options.focusViewportDuration ??
              CANVAS_VIEWPORT_TRANSITION_MS.focusReveal,
          )
        : undefined,
    },
    replaced,
  );
  if (!focus) {
    focusPresentedFramesInDesktopMode(presentedFrameIds(next));
    return;
  }
  focusPresentedFramesInDesktopMode(next.frameIds, {
    allowMirror: options.allowDesktopMirror,
    returnToWorkspace: replacedFocusReturnsToWorkspace,
    preview: placement === "preview",
  });
}

export function presentFrameAt(index: number) {
  const presentation = usePresentationStore.getState().presentation;
  if (!presentation) return;
  const activeId =
    presentation.frameIds[
      Math.min(Math.max(index, 0), presentation.frameIds.length - 1)
    ];
  if (!activeId || expandConversationCompanionStackFrame(activeId)) return;
  const focus = presentation.variant === "focus";
  const next = planPresentAt(presentation, {
    index,
    presentationBox: focus
      ? undefined
      : currentPresentationBox(presentation.variant),
  });
  if (!next) return;
  const stepping = next.activeIndex !== presentation.activeIndex;
  commitPresentation(next, {
    duration: focus
      ? focusViewportDuration(stepping ? 0 : undefined)
      : undefined,
  });
  focusPresentedFramesInDesktopMode(presentedFrameIds(next));
}

export function addFrameToFocusPresentation(frameId: string) {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") {
    enterPresentationMode("focus", [frameId], frameId);
    return;
  }
  const next = planAddFocusFrame(presentation, frameId, currentFocusPlanEnv());
  if (!next) return;
  commitPresentation(next, { duration: focusViewportDuration() });
  focusPresentedFramesInDesktopMode(next.frameIds);
  scheduleFocusPresentationReflow(250);
}

export function navigatePresentation(delta: 1 | -1) {
  const presentation = usePresentationStore.getState().presentation;
  if (!presentation) return;
  const next = Math.min(
    Math.max(presentation.activeIndex + delta, 0),
    presentation.frameIds.length - 1,
  );
  if (next === presentation.activeIndex) return;
  presentFrameAt(next);
}

/**
 * The renderer restores first and the store is written last, so a frame
 * stays focus-managed until the echoes of its restore are suppressed.
 */
function endPresentation(
  presentation: PresentationState,
  options: {
    restoreViewport?: boolean;
    preserveTemporaryFrames?: boolean;
    replaced?: boolean;
  },
) {
  const {
    restoreViewport = true,
    preserveTemporaryFrames = false,
    replaced = false,
  } = options;
  useDesktopMode.getState().clearCompactFrameOverlay();

  const temporaryFrameIds = preserveTemporaryFrames
    ? new Set<string>()
    : transientConversationFrameIds(
        selectOrderedFrames(useFramesStore.getState()),
      );
  focusAdapterFor(presentation.renderer).restore(presentation, {
    restoreViewport,
    replaced,
  });
  useSelectionStore.getState().retain((id) => !temporaryFrameIds.has(id));
  usePresentationStore.getState().setPresentation(null);
  discardFrames(temporaryFrameIds);
  if (temporaryFrameIds.size > 0) {
    useCanvasStore.getState().fitViewAll?.();
  }

  if (presentation.variant === "review") {
    void import("@/stores/review-mode-store").then(({ useReviewModeStore }) => {
      if (usePresentationStore.getState().presentation?.variant === "review") {
        return;
      }
      const review = useReviewModeStore.getState();
      if (review.isOpen) review.closeReviewMode();
    });
  }
}

export function exitPresentationMode(
  options: {
    restoreViewport?: boolean;
    preserveTemporaryFrames?: boolean;
  } = {},
) {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation) endPresentation(presentation, options);
}
